import { styled } from "goober";
import { navEnterStyles } from "../hooks/useNavTransition";
import { usePanelTransition } from "./PanelTransition";
import { PanelFrame } from "./PanelFrame";

const stepDetailsHeadingHeight = 40;

const Container = styled(PanelFrame)`
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;

  &[data-embedded="true"] {
    background: var(--chatpad-background, var(--surface-secondary));
    border: none;
    border-radius: 0;
    min-width: 0;
  }
`;

const Heading = styled("div")`
  ${navEnterStyles}
  height: ${stepDetailsHeadingHeight}px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 0.5px solid var(--border-alpha);
`;

const Subheader = styled("div")`
  height: ${stepDetailsHeadingHeight}px;
  padding: 8px;
  display: flex;
  align-items: center;
  border-bottom: 0.5px solid var(--border-alpha);
  flex-shrink: 0;
  overflow: hidden;
  animation: subheader-slide-down var(--motion-base) var(--ease-out);

  @keyframes subheader-slide-down {
    from {
      height: 0;
      border-bottom-color: transparent;
    }
    to {
      height: ${stepDetailsHeadingHeight}px;
      border-bottom-color: var(--border-alpha);
    }
  }
`;

const Body = styled("div")`
  ${navEnterStyles}
  padding: 12px;
  flex: 1;
  overflow: auto;
`;

const FlushBody = styled(Body)`
  padding: 0;
  container-type: inline-size;
`;

const Notice = styled("div")`
  height: ${stepDetailsHeadingHeight}px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  border-top: 0.5px solid var(--border-alpha);
  flex-shrink: 0;
  overflow: hidden;
`;

const Footer = styled("div")`
  ${navEnterStyles}
  height: ${stepDetailsHeadingHeight}px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 0.5px solid var(--border-alpha);
  flex-shrink: 0;
  overflow: hidden;

  &[data-animate="true"] {
    animation: footer-slide-up var(--motion-base) var(--ease-out);
  }

  &[data-animate="true"] > * {
    animation: footer-fade-in var(--motion-base) var(--ease-out);
  }

  @keyframes footer-slide-up {
    from {
      height: 0;
      border-top-color: transparent;
    }
    to {
      height: ${stepDetailsHeadingHeight}px;
      border-top-color: var(--border-alpha);
    }
  }

  @keyframes footer-fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

type Props = {
  heading: React.ReactNode;
  headingRight?: React.ReactNode;
  subheader?: React.ReactNode;
  notice?: React.ReactNode;
  footer?: React.ReactNode;
  // Footer appears based on async data too, so its slide-up can't be inferred
  // from presence; callers set this true only for the action that reveals it.
  animateFooter?: boolean;
  body: React.ReactNode;
  flushBody?: boolean;
  embedded?: boolean;
};

export function StepDetails({
  heading,
  headingRight,
  subheader,
  notice,
  footer,
  animateFooter = false,
  body,
  flushBody,
  embedded = false,
}: Props) {
  const direction = usePanelTransition();
  return (
    <Container data-embedded={embedded}>
      {heading && (
        <Heading data-nav-enter={direction}>
          {heading}
          {headingRight}
        </Heading>
      )}
      {subheader && <Subheader>{subheader}</Subheader>}
      {flushBody ? (
        <FlushBody data-nav-enter={direction}>{body}</FlushBody>
      ) : (
        <Body data-nav-enter={direction}>{body}</Body>
      )}
      {notice && <Notice>{notice}</Notice>}
      {footer && (
        <Footer
          data-nav-enter={direction}
          data-animate={animateFooter ? "true" : undefined}
        >
          {footer}
        </Footer>
      )}
    </Container>
  );
}
